import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "@/config";
import { ADD_TO_CART_LINES, UPDATE_CART_LINES } from "@/lib/shopify/queries";
import { ICartItemHeadless, TCartInfo, TSelectedVariantAndDate, TSelectedVariants, TTrackTypes, TVisitorLocation } from "@/types";
import { query } from "@/lib/shopify";
import { getAdminIdFromStoreFrontId } from "./shopify";
import { Dispatch } from "react";
import { base64Encode, sortBySku } from "./utils";
import { DISCOUNT_FUNCTIONS, createDiscountFunction } from "@/consts";
import { TRACK_TO_VARIANT_ACTION } from "./trackConfig";
import { POSTCODE_DISCOUNT_TIER_ATTRIBUTE, getPostcodeDiscountTierForCart, getVisitorLocationInfoForCart } from "./postcodeDiscount";
import { ACK_LINE_PROPERTY } from "./ackCart";
import { LARGE_GROUP_SURCHARGE_ATTRIBUTE, getLargeGroupSurchargeForCart } from "./sessionPrice";

interface CartUpdateResponse {
  cart: any;
  error?: string;
}

/**
 * Resolves which discount (if any) should be applied to a variant based on
 * its price ranking position and the URL discount parameters.
 *
 * Discount assignment rules:
 *   - "3→2→3 path" (qualifiedFor20OnThird): 20% on cheapest item only
 *   - Full qualification (both 20% + 25%): 25% on cheapest, 20% on 2nd cheapest
 *   - Only 20%: 20% on cheapest item
 *   - Only 25%: 25% on cheapest item
 *
 * Previously this logic was duplicated 4 times across the headless/Shopify
 * and with/without-visitor-location code paths.
 */
const resolveDiscountForVariant = (
  index: number,
  cheapestItemIndex: number,
  secondCheapestItemIndex: number,
  variantsCount: number,
  urlParams?: {
    qualifiedFor20?: string; qualifiedFor25?: string; qualifiedFor20OnThird?: string;
    discount20Applied?: string; discount25Applied?: string;
    bkQualifiedFor20?: string; bkQualifiedFor25?: string; bkQualifiedFor20OnThird?: string;
    bkDiscount20Applied?: string; bkDiscount25Applied?: string;
  },
): { id: string; message: string } | null => {
  if (!urlParams) return null;

  const hasHKFullQualification = urlParams.qualifiedFor20 === "true" && urlParams.qualifiedFor25 === "true";
  const hasBKFullQualification = urlParams.bkQualifiedFor20 === "true" && urlParams.bkQualifiedFor25 === "true";
  const hasFullQualification = hasHKFullQualification || hasBKFullQualification;

  const isHK20OnThirdPath = !hasFullQualification && urlParams.qualifiedFor20OnThird === "true" && urlParams.discount20Applied === "true" && urlParams.discount25Applied !== "true";
  const isBK20OnThirdPath = !hasFullQualification && urlParams.bkQualifiedFor20OnThird === "true" && urlParams.bkDiscount20Applied === "true" && urlParams.bkDiscount25Applied !== "true";
  const is20OnThirdPath = isHK20OnThirdPath || isBK20OnThirdPath;

  const has25Discount = hasFullQualification || urlParams.discount25Applied === "true" || urlParams.bkDiscount25Applied === "true";
  const has20Discount = hasFullQualification || urlParams.discount20Applied === "true" || urlParams.bkDiscount20Applied === "true";

  // The 20% discount targets the 2nd race and the 25% discount targets the 3rd race, so
  // they can only apply when that many races actually exist. Without this guard, a stale
  // discount25Applied flag (e.g. after reducing from 3 races/items down to 2) would still
  // apply the 25% "3rd race" discount to the cheapest of just 2 items.
  const canApply20 = has20Discount && variantsCount >= 2;
  const canApply25 = has25Discount && variantsCount >= 3;

  const isTheCheapestItem = index === cheapestItemIndex;
  const isSecondCheapestItem = index === secondCheapestItemIndex;

  if (is20OnThirdPath && canApply20) {
    if (isTheCheapestItem) {
      const itemPosition = variantsCount >= 3 ? 3 : 2;
      return createDiscountFunction(20, itemPosition);
    }
  } else if (canApply25 && canApply20) {
    if (isTheCheapestItem) return createDiscountFunction(25, 3);
    if (isSecondCheapestItem) return createDiscountFunction(20, 2);
  } else if (canApply20 && !canApply25) {
    if (isTheCheapestItem) return createDiscountFunction(20, 2);
  } else if (canApply25 && !canApply20) {
    if (isTheCheapestItem) return createDiscountFunction(25, 3);
  }

  return null;
};

interface AddToCartResult {
  success: boolean;
  duplicates?: TSelectedVariants[];
  error?: string;
}

export const updateCartQuantity = async (
  ids: string[],
  newQuantity: number,
  productRenderedLocation: string,
  headlessCartInfo?: { id: string },
  trackType?: TTrackTypes | null,
  selectedVariants?: TSelectedVariantAndDate | null,
  dispatch?: Dispatch<any> | null
): Promise<CartUpdateResponse> => {
  // Runtime type checking to prevent TypeError: t.reduce is not a function
  if (!Array.isArray(ids)) {
    throw new Error(`updateCartQuantity expects 'ids' to be an array, but received: ${typeof ids}`);
  }
  if (productRenderedLocation === "external") {
    return updateHeadlessCart(ids, newQuantity, headlessCartInfo?.id);
  } else {
    if (trackType && selectedVariants && dispatch) {
      return updateShopifyCart(ids, newQuantity, trackType, selectedVariants, dispatch);
    } else {
      return updateShopifyCart(ids, newQuantity);
    }
  }
};

const updateHeadlessCart = async (lineIds: string[], quantity: number, cartId?: string): Promise<CartUpdateResponse> => {
  if (!cartId) {
    throw new Error("Cart ID is required for headless cart update");
  }

  // Validate input parameters
  if (!Array.isArray(lineIds) || lineIds.length === 0) {
    throw new Error("Line IDs array is required and cannot be empty");
  }

  if (quantity < 0) {
    throw new Error("Quantity cannot be negative");
  }

  const headers = {
    "Content-Type": "application/json",
    "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
  };

  const lines = lineIds.map((id) => ({
    id,
    quantity,
  }));

  const requestOptions = {
    method: "POST",
    headers,
    body: JSON.stringify({
      query: UPDATE_CART_LINES,
      variables: {
        cartId,
        lines,
      },
    }),
  };

  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, requestOptions);
    const responseData = await response.json();

    if (response.status === 200) {
      // Check for GraphQL errors first
      if (responseData?.errors && responseData.errors.length > 0) {
        throw new Error(`GraphQL errors: ${JSON.stringify(responseData.errors)}`);
      }

      const isCartUpdated = responseData?.data?.cartLinesUpdate;

      // Check if there are userErrors in the response first
      if (isCartUpdated && isCartUpdated.userErrors && isCartUpdated.userErrors.length > 0) {
        throw new Error(`Cart update failed: ${isCartUpdated.userErrors.map((err: any) => err.message).join(", ")}`);
      }

      if (isCartUpdated) {
        // Handle case where cart might be null (e.g., when all items are removed)
        const cart = isCartUpdated.cart;
        const cartItems = cart?.lines?.edges?.map((edge: { node: any }) => edge.node) || [];
        const totalAmount = cart?.cost?.totalAmount?.amount || "0";

        return {
          cart: {
            items: cartItems,
            totalAmount: totalAmount,
          },
        };
      }

      // If we get here, the response structure is unexpected
      throw new Error(`Cart data is missing in the GraphQL response. Response structure: ${JSON.stringify(responseData?.data)}`);
    }
    throw new Error(`HTTP error: ${response.status} - ${JSON.stringify(responseData?.errors || responseData)}`);
  } catch (error) {
    // Provide more specific error messages
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error("Network error: Unable to connect to Shopify. Please check your internet connection.");
    }

    if (error instanceof SyntaxError) {
      throw new Error("Invalid response format from Shopify API");
    }

    // Re-throw the error with additional context
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    throw new Error(`Cart update failed: ${errorMessage}`);
  }
};

const updateShopifyCart = async (
  ids: string[],
  quantity: number,
  trackType: TTrackTypes | null = null,
  selectedVariants: TSelectedVariantAndDate | null = null,
  dispatch: Dispatch<any> | null = null
): Promise<CartUpdateResponse> => {
  try {
    let cartUpdateData = {
      updates: ids.reduce((acc, id) => {
        acc[id] = quantity;
        return acc;
      }, {} as Record<string, number>),
    };

    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cartUpdateData),
    });

    if (!response.ok) {
      throw new Error("Cart update failed: " + response.statusText);
    }

    const cart = await response.json();

    const { items } = cart;

    if (trackType && selectedVariants && dispatch) {
      // Use variant_id (not id) — Shopify's item.id can be a line-item key
      // that doesn't match the admin variant ID from the storefront API.
      const getCartVariantIds = (cartItems: any[]) =>
        cartItems.map((item: any) => Number(item.variant_id ?? item.id));

      const actionType = TRACK_TO_VARIANT_ACTION[trackType];
      if (actionType) {
        const cartVariantIds = items && items.length > 0 ? getCartVariantIds(items) : [];
        const remainingCartItems = items && items.length > 0
          ? selectedVariants.selectedVariants.filter((item: any) =>
              cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)),
            )
          : [];

        dispatch({
          type: actionType as any,
          payload: {
            variantsAndDate: {
              ...selectedVariants,
              selectedVariants: remainingCartItems.length > 0
                ? sortBySku([...remainingCartItems], trackType)
                : [],
            },
          },
        });
      }
    }

    miniCartCallback(false);

    if (!cart) {
      throw new Error("Cart update failed: unknown error");
    }

    return { cart };
  } catch (error) {
    console.error("Cart update failed:", error);
    throw error;
  }
};

export const dispatchCartUpdates = (dispatch: any, cartData: any, productRenderedLocation: string, membershipInfo?: any, customerInfo?: any) => {
  const hasItemsInCart = cartData.items && cartData.items.length > 0;

  // Update isAnyItemsAvailableInCart based on current cart state
  dispatch({
    type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
    payload: { isAnyItemsAvailableInCart: hasItemsInCart },
  });

  if (productRenderedLocation === "external") {
    dispatch({
      type: "SET_CARTITEMS_HEADLESS",
      payload: {
        headlessCart: cartData.items as ICartItemHeadless[],
      },
    });

    dispatch({
      type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
      payload: {
        headlessCartTotalAmount: cartData.totalAmount,
      },
    });

    // Update membership state for headless cart
    if (membershipInfo && customerInfo) {
      const isMembershipItemStillInCart =
        cartData.items && cartData.items.length > 0 && cartData.items.some((item: any) => item.merchandise?.product?.title?.includes("Membership"));
      const { isCustomerLogged, isCustomerActiveSubscriber } = customerInfo;

      const isMembershipTrue = (isCustomerLogged && isCustomerActiveSubscriber) || isMembershipItemStillInCart;

      // Only close popup if membership is no longer valid and popup is currently open
      // Don't override popup state during normal cart operations to prevent infinite loops
      const shouldClosePopup = !isMembershipTrue && membershipInfo.isPopupOpen;

      dispatch({
        type: "SET_MEMBERSHIP_INFO",
        payload: {
          membershipInfo: {
            ...membershipInfo,
            isMembershipItemAddedToCart: isMembershipTrue,
            eligibleVariants: isMembershipTrue ? membershipInfo.eligibleVariants : [],
            isItemAddedToCart: isMembershipTrue && membershipInfo.isItemAddedToCart,
            isPopupOpen: shouldClosePopup ? false : membershipInfo.isPopupOpen,
          },
        },
      });
    }
  } else {
    if (!cartData.items.length) {
      dispatch({
        type: "SET_IS_CART_EMPTY",
        payload: {
          isCartEmpty: true,
        },
      });
    }

    // Update membership state for Shopify cart
    if (membershipInfo && customerInfo) {
      const isMembershipItemStillInCart = cartData.items && cartData.items.length > 0 && cartData.items.some((item: any) => item.product_title?.includes("Membership"));
      const { isCustomerLogged, isCustomerActiveSubscriber } = customerInfo;

      const isMembershipTrue = (isCustomerLogged && isCustomerActiveSubscriber) || isMembershipItemStillInCart;

      // Only close popup if membership is no longer valid and popup is currently open
      // Don't override popup state during normal cart operations to prevent infinite loops
      const shouldClosePopup = !isMembershipTrue && membershipInfo.isPopupOpen;

      dispatch({
        type: "SET_MEMBERSHIP_INFO",
        payload: {
          membershipInfo: {
            ...membershipInfo,
            eligibleVariants: isMembershipTrue ? membershipInfo.eligibleVariants : [],
            isMembershipItemAddedToCart: isMembershipTrue,
            isItemAddedToCart: isMembershipTrue && membershipInfo.isItemAddedToCart,
            isPopupOpen: shouldClosePopup ? false : membershipInfo.isPopupOpen,
          },
        },
      });
    }

    const cartUpdatedEvent = new CustomEvent("cart:updated", {
      detail: { cart: cartData },
    });
    window.dispatchEvent(cartUpdatedEvent);
  }
};

export const handleAddToCartClick = async (
  selectedVariants: TSelectedVariants[],
  numberOfSeats: number,
  productRenderedLocation: string,
  headlessCart: ICartItemHeadless[],
  headlessCartInfo: TCartInfo,
  dispatch: Dispatch<any>,
  displayMiniCart: boolean = false,
  visitorLocation: TVisitorLocation | null = null,
  trackType?: TTrackTypes | null,
  urlParams?: {
    // HyperKarting discount params
    discountOffered?: string;
    discount15Applied?: string;
    discount20Applied?: string;
    discount25Applied?: string;
    qualifiedFor20?: string; // User has qualified for 20% on 2nd race
    qualifiedFor25?: string; // User has qualified for 25% on 3rd race
    qualifiedFor20OnThird?: string; // 20% on 3rd race via 3→2→3 path
    // BattleKart discount params
    bkDiscountOffered?: string;
    bkDiscount15Applied?: string;
    bkDiscount20Applied?: string;
    bkDiscount25Applied?: string;
    bkQualifiedFor20?: string; // User has qualified for 20% on 2nd session
    bkQualifiedFor25?: string; // User has qualified for 25% on 3rd session
    bkQualifiedFor20OnThird?: string; // 20% on 3rd session via 3→2→3 path
  },
  // Optional per-variant quantities (from edit mode) - overrides numberOfSeats for specific variants
  perVariantQuantities?: Record<string, number>,
  // Active Kids session, when the customer has a validated voucher. Written as a line
  // property so orders/paid can still find the session if a wallet checkout drops the
  // cart attribute.
  ackSessionId?: string | null
): Promise<AddToCartResult> => {
  // Helper to get quantity for a specific variant
  const getQuantityForVariant = (variantId: string): number => {
    if (perVariantQuantities && perVariantQuantities[variantId]) {
      return perVariantQuantities[variantId];
    }
    return numberOfSeats;
  };
  // Decided by the seats-screen count, not the per-variant edit quantities.
  const largeGroupSurcharge = getLargeGroupSurchargeForCart(numberOfSeats);
  try {
    if (!selectedVariants || selectedVariants.length === 0) {
      return {
        success: false,
        error: "Need to select a time",
      };
    }

    if (productRenderedLocation === "external") {
      // Check for duplicates against current cart items
      const cartVariantIds = headlessCart?.map((item) => item.merchandise.id) || [];
      // Find variants that are already in the cart
      const duplicates = selectedVariants.filter((variant) => cartVariantIds.includes(variant.variantsSelectedObject.id));
      const variantsToAdd = selectedVariants.filter((variant) => !cartVariantIds.includes(variant.variantsSelectedObject.id));

      if (variantsToAdd.length === 0) {
        return {
          success: false,
          duplicates,
        };
      }

      // Check if discount should be applied for HyperKarting (Main Track)
      const shouldApplyHKDiscount =
        trackType === "MAIN_TRACK" &&
        urlParams?.discountOffered === "true" &&
        (urlParams?.discount15Applied === "true" || urlParams?.discount20Applied === "true" || urlParams?.discount25Applied === "true");

      // Check if discount should be applied for BattleKart
      const shouldApplyBKDiscount =
        trackType === "BATTLE_KART_TRACK" &&
        urlParams?.bkDiscountOffered === "true" &&
        (urlParams?.bkDiscount15Applied === "true" || urlParams?.bkDiscount20Applied === "true" || urlParams?.bkDiscount25Applied === "true");

      // Collect all applicable discount functions (largest first for cheapest item)
      const discountFunctions: { id: string; message: string }[] = [];
      if (shouldApplyHKDiscount) {
        if (urlParams?.discount25Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_4); // 25% goes to cheapest
        }
        if (urlParams?.discount20Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_3); // 20% goes to 2nd cheapest
        }
      } else if (shouldApplyBKDiscount) {
        if (urlParams?.bkDiscount25Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_4); // 25% goes to cheapest
        }
        if (urlParams?.bkDiscount20Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_3); // 20% goes to 2nd cheapest
        }
      }

      // Create an array of variants with their indices and prices, then sort by price (ascending - cheapest first)
      const variantPriceRanking = variantsToAdd
        .map((variant, index) => ({
          index,
          price: parseFloat(variant.variantsSelectedObject.priceV2.amount),
        }))
        .sort((a, b) => a.price - b.price);

      // Get the indices of cheapest and 2nd cheapest items directly
      const cheapestItemIndex = variantPriceRanking[0]?.index;
      const secondCheapestItemIndex = variantPriceRanking[1]?.index;

      const lines = {
        items: variantsToAdd.map((variant, index) => {
          const isXLeftAutomationEnabled = variant.variantsSelectedObject.xLeftAutomation?.value !== "disabled";
          const attributes: Array<{ key: string; value: string }> = [
            { key: "_inventory_quantity", value: base64Encode(String(variant.variantsSelectedObject.quantityAvailable)) },
            { key: "_locked_price", value: base64Encode(String(variant.variantsSelectedObject.priceV2.amount)) },
            { key: "_x_left_automation_enabled", value: base64Encode(String(isXLeftAutomationEnabled)) },
          ];

          // Requiring a non-zero discount keeps a stale cookie for an ineligible
          // postcode from reaching CartTransform. Variants on a $59 -5 PO / -10 PO tier
          // are already postcode-adjusted, so they carry the tier attribute instead.
          const visitorLocationInfo = getVisitorLocationInfoForCart(visitorLocation, variant.variantsSelectedObject.postcodeDiscountAdjustment);
          if (visitorLocationInfo) {
            attributes.push({ key: "_visitorLocationInfo", value: base64Encode(JSON.stringify(visitorLocationInfo)) });
          }

          // Wallet fallback for Active Kids. Shop Pay and Apple Pay may drop the cart
          // attribute, and the discount has already been applied by then - orders/paid
          // would see no session and give it away. Line properties survive checkout.
          if (ackSessionId) {
            attributes.push({ key: ACK_LINE_PROPERTY, value: base64Encode(ackSessionId) });
          }

          const postcodeDiscountTier = getPostcodeDiscountTierForCart(variant.variantsSelectedObject.postcodeDiscountAdjustment);
          if (postcodeDiscountTier) {
            attributes.push({ key: POSTCODE_DISCOUNT_TIER_ATTRIBUTE, value: postcodeDiscountTier });
          }

          if (largeGroupSurcharge) {
            attributes.push({ key: LARGE_GROUP_SURCHARGE_ATTRIBUTE, value: largeGroupSurcharge });
          }

          if ((shouldApplyHKDiscount || shouldApplyBKDiscount) && discountFunctions.length > 0) {
            const discountToApply = resolveDiscountForVariant(index, cheapestItemIndex, secondCheapestItemIndex, variantsToAdd.length, urlParams);
            if (discountToApply) {
              attributes.push({ key: "_discountFunction", value: base64Encode(JSON.stringify(discountToApply)) });
            }
          }

          return {
            merchandiseId: variant.variantsSelectedObject.id,
            quantity: getQuantityForVariant(variant.variantsSelectedObject.id),
            attributes,
          };
        }),
      };

      const addToCart = await query(ADD_TO_CART_LINES, {
        cartId: headlessCartInfo.id,
        lines: lines.items,
      });

      const isAddedToCart = addToCart?.data?.cartLinesAdd;

      if (isAddedToCart) {
        const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
        dispatch({
          type: "SET_CARTITEMS_HEADLESS",
          payload: {
            headlessCart: [...cartItems] as ICartItemHeadless[],
          },
        });

        const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
        dispatch({
          type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
          payload: {
            headlessCartTotalAmount: cartTotalAmount,
          },
        });
        if (displayMiniCart) {
          dispatch({
            type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
            payload: {
              isHeadlessMiniCartIsShowing: true,
            },
          });
        }
        return { success: true };
      } else {
        console.error("isAddedToCart Cart data is missing in the GraphQL response.", isAddedToCart);
        return {
          success: false,
          error: "Failed to add items to cart",
        };
      }
    } else {
      // Check if discount should be applied for HyperKarting (Main Track)
      const shouldApplyHKDiscount =
        trackType === "MAIN_TRACK" &&
        urlParams?.discountOffered === "true" &&
        (urlParams?.discount15Applied === "true" || urlParams?.discount20Applied === "true" || urlParams?.discount25Applied === "true");

      // Check if discount should be applied for BattleKart
      const shouldApplyBKDiscount =
        trackType === "BATTLE_KART_TRACK" && urlParams?.bkDiscountOffered === "true" && (urlParams?.bkDiscount20Applied === "true" || urlParams?.bkDiscount25Applied === "true");

      // Collect all applicable discount functions (largest first for cheapest item)
      const discountFunctions: { id: string; message: string }[] = [];
      if (shouldApplyHKDiscount) {
        if (urlParams?.discount25Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_4); // 25% goes to cheapest
        }
        if (urlParams?.discount20Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_3); // 20% goes to 2nd cheapest
        }
      } else if (shouldApplyBKDiscount) {
        if (urlParams?.bkDiscount25Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_4); // 25% goes to cheapest
        }
        if (urlParams?.bkDiscount20Applied === "true") {
          discountFunctions.push(DISCOUNT_FUNCTIONS.LINE_ITEMS_LESS_THAN_3); // 20% goes to 2nd cheapest
        }
      }

      const variantPriceRanking = selectedVariants
        .map((variant, index) => ({
          index,
          price: parseFloat(variant.variantsSelectedObject.priceV2.amount),
        }))
        .sort((a, b) => a.price - b.price);

      const cheapestItemIndex = variantPriceRanking[0]?.index;
      const secondCheapestItemIndex = variantPriceRanking[1]?.index;

      const formData = {
        items: selectedVariants.map((variant, index) => {
          const isXLeftAutomationEnabled = variant.variantsSelectedObject.xLeftAutomation?.value !== "disabled";
          const properties: any = {
            _inventory_quantity: base64Encode(String(variant.variantsSelectedObject.quantityAvailable)),
            _locked_price: base64Encode(String(variant.variantsSelectedObject.priceV2.amount)),
            _x_left_automation_enabled: base64Encode(String(isXLeftAutomationEnabled)),
          };

          // Requiring a non-zero discount keeps a stale cookie for an ineligible
          // postcode from reaching CartTransform. Variants on a $59 -5 PO / -10 PO tier
          // are already postcode-adjusted, so they carry the tier attribute instead.
          const visitorLocationInfo = getVisitorLocationInfoForCart(visitorLocation, variant.variantsSelectedObject.postcodeDiscountAdjustment);
          if (visitorLocationInfo) {
            properties._visitorLocationInfo = base64Encode(JSON.stringify(visitorLocationInfo));
          }

          // See the headless branch above - the same fallback, the other cart mode.
          if (ackSessionId) {
            properties[ACK_LINE_PROPERTY] = base64Encode(ackSessionId);
          }

          const postcodeDiscountTier = getPostcodeDiscountTierForCart(variant.variantsSelectedObject.postcodeDiscountAdjustment);
          if (postcodeDiscountTier) {
            properties[POSTCODE_DISCOUNT_TIER_ATTRIBUTE] = postcodeDiscountTier;
          }

          if (largeGroupSurcharge) {
            properties[LARGE_GROUP_SURCHARGE_ATTRIBUTE] = largeGroupSurcharge;
          }

          if ((shouldApplyHKDiscount || shouldApplyBKDiscount) && discountFunctions.length > 0) {
            const discountToApply = resolveDiscountForVariant(index, cheapestItemIndex, secondCheapestItemIndex, selectedVariants.length, urlParams);
            if (discountToApply) {
              properties._discountFunction = base64Encode(JSON.stringify(discountToApply));
            }
          }

          return {
            id: getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id),
            quantity: getQuantityForVariant(variant.variantsSelectedObject.id),
            properties,
          };
        }),
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed: ${response.status} ${response.statusText}`);
      }

      const addedItems = await response.json();

      // Fetch the complete cart data after adding items
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      if (cart && addedItems) {
        miniCartCallback(false);
        const { items_subtotal_price } = cart;

        if (items_subtotal_price) {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: items_subtotal_price,
            },
          });
        }

        // Dispatch cart:updated event to notify all listeners
        const cartUpdatedEvent = new CustomEvent("cart:updated", {
          detail: { cart },
        });
        window.dispatchEvent(cartUpdatedEvent);

        return { success: true };
      }
      return {
        success: false,
        error: "Failed to add items to cart",
      };
    }
  } catch (error) {
    console.error("Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};

// Specific function for adding membership products that require selling plans
export const handleAddMembershipToCart = async (
  membershipVariant: TSelectedVariants,
  sellingPlanId: string,
  productRenderedLocation: string,
  headlessCart: ICartItemHeadless[],
  headlessCartInfo: TCartInfo,
  dispatch: Dispatch<any>
): Promise<AddToCartResult> => {
  try {
    if (productRenderedLocation === "external") {
      // Check for duplicates against current cart items
      const cartVariantIds = headlessCart?.map((item) => item.merchandise.id) || [];

      if (cartVariantIds.includes(membershipVariant.variantsSelectedObject.id)) {
        return {
          success: false,
          duplicates: [membershipVariant],
        };
      }

      const lines = {
        items: [
          {
            merchandiseId: membershipVariant.variantsSelectedObject.id,
            quantity: 1,
            sellingPlanId: sellingPlanId,
            attributes: [
              {
                key: "_locked_price",
                value: base64Encode(String(membershipVariant.variantsSelectedObject.priceV2.amount)),
              },
            ],
          },
        ],
      };

      const addToCart = await query(ADD_TO_CART_LINES, {
        cartId: headlessCartInfo.id,
        lines: lines.items,
      });

      const isAddedToCart = addToCart?.data?.cartLinesAdd;

      if (isAddedToCart) {
        const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
        dispatch({
          type: "SET_CARTITEMS_HEADLESS",
          payload: {
            headlessCart: [...cartItems] as ICartItemHeadless[],
          },
        });

        const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
        dispatch({
          type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
          payload: {
            headlessCartTotalAmount: cartTotalAmount,
          },
        });

        return { success: true };
      } else {
        console.error("Failed to add membership to cart:", isAddedToCart);
        return {
          success: false,
          error: "Failed to add membership to cart",
        };
      }
    } else {
      // For regular Shopify cart - extract numeric ID from GraphQL GID
      const numericSellingPlanId = sellingPlanId.split("/").pop();

      let formData = {
        items: [
          {
            id: getAdminIdFromStoreFrontId(membershipVariant.variantsSelectedObject.id),
            quantity: 1,
            selling_plan: parseInt(numericSellingPlanId || "0", 10),
            properties: {
              _locked_price: base64Encode(String(membershipVariant.variantsSelectedObject.priceV2.amount)),
            },
          },
        ],
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed: ${response.status} ${response.statusText}`);
      }

      const addedItems = await response.json();

      // Fetch the complete cart data after adding items
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      if (cart && addedItems) {
        miniCartCallback(false);
        const { items_subtotal_price } = cart;

        if (items_subtotal_price) {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: items_subtotal_price,
            },
          });
        }

        // Dispatch cart:updated event to notify all listeners
        const cartUpdatedEvent = new CustomEvent("cart:updated", {
          detail: { cart },
        });
        window.dispatchEvent(cartUpdatedEvent);

        return { success: true };
      }
      return {
        success: false,
        error: "Failed to add membership to cart",
      };
    }
  } catch (error) {
    console.error("Error adding membership to cart:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};
